import { useId } from "react";
import type { LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import type { NavItemRestriction } from "../types";
import { SidebarIcon } from "../atoms/SidebarIcon";
import { SidebarTooltip } from "../atoms/SidebarTooltip";

interface SidebarNavItemProps {
  /** Lucide icon component to display */
  icon: LucideIcon;
  /** Display label for the navigation item */
  label: string;
  /** URL path for navigation (uses react-router-dom Link) */
  href?: string;
  /** Click handler for items without href */
  onClick?: () => void;
  /** Whether this item is currently active */
  isActive?: boolean;
  /** WHA-3430: when true, renders grayed-out and non-clickable */
  disabled?: boolean;
  /**
   * Dimmed but still focusable and clickable: activating it calls
   * `onActivate` instead of navigating, and the tooltip — shown expanded or
   * collapsed — gives the reason.
   */
  restricted?: NavItemRestriction;
  /** Whether the sidebar is collapsed (required for tooltip/label display) */
  isCollapsed: boolean;
  /** WHA-3339: render a small red dot in the icon's top-right corner */
  showUnreadDot?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * SidebarNavItem - Molecule component
 *
 * A complete navigation item that composes SidebarIcon and SidebarTooltip atoms.
 * Displays tooltip on hover when collapsed, label when expanded.
 * Supports both href navigation (via Link) and onClick actions.
 *
 * Part of the atomic design system for NavigationSidebar.
 *
 * @example
 * ```tsx
 * import { Home, Settings } from 'lucide-react';
 *
 * // Basic navigation item
 * <SidebarNavItem
 *   icon={Home}
 *   label="Dashboard"
 *   href="/dashboard"
 *   isCollapsed={false}
 * />
 *
 * // Active state with collapsed sidebar
 * <SidebarNavItem
 *   icon={Settings}
 *   label="Settings"
 *   href="/settings"
 *   isActive={true}
 *   isCollapsed={true}
 * />
 *
 * // Action item without navigation
 * <SidebarNavItem
 *   icon={LogOut}
 *   label="Logout"
 *   onClick={handleLogout}
 *   isCollapsed={false}
 * />
 * ```
 */
export function SidebarNavItem({
  icon,
  label,
  href,
  onClick,
  isActive = false,
  disabled = false,
  restricted,
  isCollapsed,
  showUnreadDot = false,
  className,
}: SidebarNavItemProps) {
  const reasonId = useId();
  const baseStyles = cn(
    "flex h-[1.875rem] w-full items-center gap-3 overflow-hidden rounded-md px-[0.9375rem] text-[0.75rem] font-medium transition-colors",
    // The hover tint is for the other rows: on the active one it would sit
    // over the white lift and read as deselecting it.
    !isActive && "hover:bg-foreground/5 hover:text-foreground",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    // Rows give up height (down to 22px, which still clears the 18px icon)
    // before the column scrolls or the footer gets pushed into them.
    "min-h-[1.375rem] shrink",
    // White on the light-grey rail is barely a contrast step, so the active
    // item also carries a red rule on its left edge (an inset shadow, clipped
    // to the radius) and an outline where forced colours drop both fills.
    isActive &&
      "bg-nav-active text-foreground shadow-[inset_2px_0_0_hsl(var(--primary)),0_1px_2px_0_rgb(0_0_0/0.05)] forced-colors:outline forced-colors:outline-1",
    // WHA-3430: standard disabled styling — grayed out, non-clickable, no hover.
    disabled && "pointer-events-none opacity-50",
    // Dimmed like `disabled`, but it keeps its pointer and focus so the
    // refusal can be explained.
    restricted && "cursor-not-allowed opacity-50 hover:bg-transparent hover:text-inherit",
    className
  );

  const content = (
    <>
      <span className="relative inline-flex shrink-0">
        <SidebarIcon icon={icon} size="md" />
        {showUnreadDot && (
          <span
            aria-label="Unread mentions"
            className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-nav"
          />
        )}
      </span>
      <span
        className={cn(
          "min-w-0 truncate transition-opacity duration-200",
          isCollapsed ? "opacity-0" : "opacity-100"
        )}
      >
        {label}
      </span>
    </>
  );

  if (restricted) {
    // Always tooltipped: expanded, the label is visible and the reason is
    // what's missing; collapsed, the tooltip has to carry both.
    return (
      <SidebarTooltip content={isCollapsed ? `${label}: ${restricted.reason}` : restricted.reason}>
        <button
          type="button"
          aria-disabled="true"
          // The reason is a description, not part of the name.
          aria-label={label}
          aria-describedby={reasonId}
          onClick={restricted.onActivate}
          className={baseStyles}
        >
          {content}
          <span id={reasonId} className="sr-only">
            {restricted.reason}
          </span>
        </button>
      </SidebarTooltip>
    );
  }

  // Wrap with tooltip only when collapsed
  const wrappedContent = (
    <SidebarTooltip content={label} disabled={!isCollapsed}>
      {disabled ? (
        // WHA-3430: render as an inert element so it is fully non-clickable
        // regardless of whether the item navigates (href) or toggles state.
        <span className={baseStyles} aria-disabled="true">
          {content}
        </span>
      ) : href ? (
        <Link to={href} className={baseStyles}>
          {content}
        </Link>
      ) : (
        <button type="button" onClick={onClick} className={baseStyles}>
          {content}
        </button>
      )}
    </SidebarTooltip>
  );

  return wrappedContent;
}
